import '@/assets/main.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { setupSentry, setupVueErrorHandler, setupGlobalErrorHandler } from '@/plugins/sentry'
import App from '@/app/App.vue'
import { useI18n } from '@/i18n/messages'
import { setupRouterSeo } from '@/modules/common/public'
import { createAppRouter } from '@/app/router/createAppRouter'
import { configureAuthTransport } from '@/app/providers/configureAuthTransport'

const initializeMonitoring = async () => {
  try {
    console.log('Monitoring initialization completed')
  } catch (error) {
    console.error('Failed to initialize monitoring:', error)
  }
}

export function bootstrapApp(): void {
  const app = createApp(App)
  const pinia = createPinia()
  const router = createAppRouter()

  app.use(pinia)
  configureAuthTransport()
  app.use(router)
  setupRouterSeo(router)

  // Sentry handlers must remain the app-level error sinks. Noise filtering
  // (browser extensions) lives inside setupVueErrorHandler / setupGlobalErrorHandler.
  setupSentry(app, router)
  setupVueErrorHandler(app)
  setupGlobalErrorHandler()

  const { t } = useI18n()
  const globalProperties = app.config.globalProperties as { $t?: typeof t }
  globalProperties.$t = t

  app.mount('#app')

  setTimeout(() => {
    initializeMonitoring().catch(console.error)
  }, 100)

  if ('serviceWorker' in navigator) {
    if (import.meta.env.PROD) {
      // updateViaCache: 'none' - браузер всегда проверяет свежий sw.js после деплоя
      navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch((error) => {
        console.warn('Service Worker registration failed:', error)
      })
    } else {
      // Удаляем worker от старой production-сборки, который может перехватывать dev-ассеты.
      const cleanupDevServiceWorker = async (): Promise<void> => {
        const registrations = await navigator.serviceWorker.getRegistrations()
        const cacheNames = await caches.keys()
        const labActiveCacheNames = cacheNames.filter((cacheName) =>
          cacheName.startsWith('labactive-'),
        )

        await Promise.all(registrations.map((registration) => registration.unregister()))
        await Promise.all(labActiveCacheNames.map((cacheName) => caches.delete(cacheName)))

        if (registrations.length > 0 || labActiveCacheNames.length > 0) {
          const reloadKey = 'labactive-dev-service-worker-cleaned'
          if (sessionStorage.getItem(reloadKey) !== '1') {
            sessionStorage.setItem(reloadKey, '1')
            window.location.reload()
          }
        }
      }

      cleanupDevServiceWorker().catch((error) => {
        console.warn('Development Service Worker cleanup failed:', error)
      })
    }
  }

  window.addEventListener('load', () => {
    if (window.performance && window.performance.timing) {
      const timing = window.performance.timing
      const loadTime = timing.loadEventEnd - timing.navigationStart
      console.log('Page load time:', loadTime)
    }
  })

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      console.log('Page hidden')
    } else {
      console.log('Page visible')
    }
  })

  if (import.meta.env.DEV) {
    const createDevTools = async () => {
      try {
        const { useAnalytics, usePerformance } = await import('@/modules/analytics/public')
        const { useErrorTracking } = await import('@/modules/common/public')

        return {
          analytics: useAnalytics(),
          performance: usePerformance(),
          errorTracking: useErrorTracking(),
        }
      } catch (error) {
        console.warn('Dev tools not available:', error)
        return {}
      }
    }

    createDevTools().then((tools) => {
      ;(window as unknown as { labactive?: unknown }).labactive = tools as unknown
      console.log('LabActive monitoring tools available at window.labactive')
    })
  }
}
